// ============================================
// LIGA AJUPAM - Supabase Client
// ============================================

import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';
import { fetchWithStaleGuard } from '../../utils/staleAppGuard';

// Validar variables de entorno
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env.local file.'
  );
}

/**
 * Supabase client singleton
 * Configurado con tipos personalizados de la base de datos
 */
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Configuración personalizada de autenticación
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false,
    storage: window.localStorage,
  },
  db: {
    schema: 'public',
  },
  global: {
    // v6.11.218: si un pedido vuelve rechazado porque la base ya no muestra
    // dni/phone/birth_date, aparece "Recargar" en vez de una pantalla vacía.
    fetch: fetchWithStaleGuard,
    headers: {
      'x-application-name': 'liga-ajupam',
      // Accept header removido (v4.7.3) - causaba error en Storage uploads:
      // "mime type application/json, image/jpeg is not supported"
      // Supabase maneja Accept automaticamente segun el tipo de operacion
      'Prefer': 'return=representation',
    },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,  // Límite de eventos para evitar sobrecarga
    },
  },
});

/**
 * Helper para verificar si estamos usando datos mock o reales
 */
export const isUsingMockData = (): boolean => {
  return import.meta.env.VITE_USE_MOCK_DATA === 'true';
};

/**
 * Helper para manejar errores de Supabase de forma consistente
 * Convierte errores técnicos en mensajes amigables para el usuario
 */
export const handleSupabaseError = (error: any): Error => {
  console.error('Supabase Error:', error);

  const errorMessage = error?.message || error?.error_description || '';

  // v6.11.85 — Conservar el SQLSTATE. Antes esta función devolvía siempre un
  // `new Error(texto)` pelado, así que el código de Postgres se perdía y el
  // frontend sólo podía adivinar por el texto del mensaje. Desde el barrido de
  // aislamiento por liga eso importa: los guards lanzan con 42501
  // (insufficient_privilege) justamente para que la UI pueda distinguir "no
  // tenés permisos" de cualquier otro fallo sin matchear prosa.
  const fail = (msg: string): Error => {
    const err = new Error(msg);
    if (error?.code) (err as Error & { code?: string }).code = error.code;
    return err;
  };

  // Errores de RLS (Row Level Security)
  if (errorMessage.includes('row-level security') || errorMessage.includes('RLS')) {
    if (errorMessage.includes('photo_approvals')) {
      return fail('Error al procesar la foto. Por favor, intenta subir la foto nuevamente.');
    }
    if (errorMessage.includes('INSERT')) {
      return fail('No tienes permiso para realizar esta acción. Verifica que estés logueado correctamente.');
    }
    if (errorMessage.includes('UPDATE')) {
      return fail('No tienes permiso para modificar este registro.');
    }
    return fail('Error de permisos. Por favor, intenta nuevamente o contacta al administrador.');
  }

  // Errores de storage
  if (errorMessage.includes('storage') || errorMessage.includes('bucket')) {
    return fail('Error al subir el archivo. Verifica que el formato sea válido (JPG, PNG) y el tamaño sea menor a 5MB.');
  }

  // Errores de conexión
  if (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('CORS')) {
    return fail('Error de conexión. Verifica tu conexión a internet e intenta nuevamente.');
  }

  // Errores de validación
  if (errorMessage.includes('duplicate') || errorMessage.includes('unique')) {
    return fail('Este registro ya existe. Verifica los datos e intenta nuevamente.');
  }

  // Errores de FK (foreign key)
  if (errorMessage.includes('foreign key') || errorMessage.includes('violates')) {
    return fail('Error de datos relacionados. Algunos datos necesarios no existen.');
  }

  // Mensaje genérico si tiene mensaje
  if (errorMessage) {
    return fail(errorMessage);
  }

  return fail('Ocurrió un error inesperado. Por favor, intenta nuevamente.');
};

/**
 * Helper para ejecutar queries con manejo de errores
 */
export const executeQuery = async <T>(
  queryFn: () => Promise<{ data: T | null; error: any }>
): Promise<T> => {
  const { data, error } = await queryFn();

  if (error) {
    throw handleSupabaseError(error);
  }

  if (!data) {
    throw new Error('No data returned from query');
  }

  return data;
};

export default supabase;
